import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { WbsItemSearchConditionVO } from '../../../../domain/value-object/WbsItemSearchConditionVo'
import { useSearchConditionAndColumnStateStorageService } from '../../../../services/storage-services/ui-states/searchConditionAndColumnStateStorageService'
import { useWbsItemSearchConditionUrlTransformService } from '../../../../services/transform-service/wbsItemSearchConditionUrlTransformService'
import { getUrlQueryObject } from '../../../../utils/urls'
import { WbsItemSearchSearchCondition } from './useSearchCondition'
import { URL_PARAMS_SHARE_SAVED_UI_STATE } from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'

const wbsItemSearchDefaultSearchCondition = {
  searchText: '',
  types: [WbsItemType.TASK],
  status: [
    WbsItemStatus.TODO,
    WbsItemStatus.DOING,
    WbsItemStatus.REVIEW,
    WbsItemStatus.DONE,
  ],
}

export type SearchConditionRestoreType =
  | 'PERMANENT'
  | 'URL_QUERY'
  | 'UI_STATE'
  | 'DID_NOT_RESTORE'

export const useRestoredSearchCondition = (
  functionUuid: string,
  projectUuid: string,
  fetchWbsItemSearchConditionVO: (
    functionUuid: string,
    projectUuid: string
  ) => Promise<WbsItemSearchConditionVO | undefined>
) => {
  const [initialized, setInitialized] = useState<boolean>(false)
  const [restoredSearchCondition, setRestoredSearchCondition] =
    useState<WbsItemSearchSearchCondition>(wbsItemSearchDefaultSearchCondition)
  const [restoreType, setRestoreType] =
    useState<SearchConditionRestoreType>('DID_NOT_RESTORE')

  const searchConditionStorageService =
    useSearchConditionAndColumnStateStorageService(functionUuid, projectUuid)
  const wbsItemSearchConditionUrlTransformer =
    useWbsItemSearchConditionUrlTransformService()

  const restorePermanentUiState = useCallback(async () => {
    const urlObject = getUrlQueryObject()
    if (!urlObject) return undefined
    const code = urlObject.arg
    if (!code) return undefined
    const permanentUiState = await searchConditionStorageService.getByCode(code)
    if (!permanentUiState) return undefined
    return permanentUiState.value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchConditionStorageService.getByCode])

  const restoreFromURLQuery = useCallback(() => {
    const conditionFromUrl =
      wbsItemSearchConditionUrlTransformer.restoreFromUrl()
    // URL_PARAMS_SHARE_SAVED_UI_STATE is not a search condition parameter, so exclude it.
    delete conditionFromUrl[URL_PARAMS_SHARE_SAVED_UI_STATE]
    if (!conditionFromUrl || Object.keys(conditionFromUrl).length === 0) {
      return undefined
    }
    return conditionFromUrl
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const restoreFromUiState = useCallback(async () => {
    const storedSearchCondition = await fetchWbsItemSearchConditionVO(
      functionUuid,
      projectUuid
    )
    // Return undefined only when there is no stored search condition in the database.
    // When user clears all conditions, storedSearchCondition has an empty object {}, and will pass through if statement.
    if (!storedSearchCondition) {
      return undefined
    }
    return storedSearchCondition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionUuid, projectUuid])

  useEffect(() => {
    const restore = async () => {
      const permanentUiState = await restorePermanentUiState()
      if (permanentUiState) {
        setRestoredSearchCondition(permanentUiState.searchCondition)
        setRestoreType('PERMANENT')
        setInitialized(true)
        return
      }
      const searchConditionInURLQuery = restoreFromURLQuery()
      if (searchConditionInURLQuery) {
        setRestoredSearchCondition(searchConditionInURLQuery)
        setRestoreType('URL_QUERY')
        setInitialized(true)
        return
      }
      const storedSearchCondition = await restoreFromUiState()
      if (storedSearchCondition) {
        setRestoredSearchCondition(storedSearchCondition)
        setRestoreType('UI_STATE')
        setInitialized(true)
        return
      }
      setInitialized(true)
    }
    restore()
  }, [restorePermanentUiState, restoreFromURLQuery, restoreFromUiState])

  const result = useMemo(
    () => ({
      initialized,
      restoredSearchCondition,
      restoreType,
    }),
    [initialized, restoredSearchCondition, restoreType]
  )
  return result
}
